// @flow
import {
    PREDEFINED_ATTRIBS,
    ATTRIB_TYPE_ENUM_CUSTOM,
    CUSTOM_LINK_PREFIX
} from './constants';

import updateCustomAttributionLinks from '../api/updateCustomAttributionLinks';
import addCustomAttributionLinks from '../api/addCustomAttributionLinks';

type Attrib = {
    attribId: number | null,
    orgId: number,
    typeEnum: number,
    typeLabel: string,
    typeSlug: string | null,
    customText: string | null,
    isCustom: boolean,
    order: number
};

export const createAttribObject = (
    config: Attrib,
    isCustom: boolean = false,
    orgId: number | null = null
) => {
    return isCustom
        ? _createCustomAttrib(config, orgId)
        : _createPredefinedAttrib(config, orgId);
};

const _createPredefinedAttrib = (config: object, orgId: number) => {
    return {
        orgId: orgId,
        typeEnum: config.typeEnum,
        typeLabel: config.typeLabel,
        typeSlug: config.typeSlug,
        customText: null,
        attribQueryParam: `${orgId}-${config.typeSlug}`,
        copyMessageText: 'Copy',
        isCustom: false
    };
};

const _createCustomAttrib = (config: object, orgId: number) => {
    // Strip the prefix that's stored in the db because
    // it's automatically prepended on save.
    let prefix = CUSTOM_LINK_PREFIX;
    let customPrefixRegexp = new RegExp(`(${CUSTOM_LINK_PREFIX}-)`);
    let text = config.text || '';
    if (config.id && text.search(customPrefixRegexp) > -1) {
        prefix = null;
        text = text.replace(customPrefixRegexp, '');
    }

    return {
        attribId: config.id || _getHash(),
        orgId: config.orgId || orgId,
        typeEnum: ATTRIB_TYPE_ENUM_CUSTOM,
        typeLabel: 'Custom',
        typeSlug: 'custom',
        customText: text,
        originalText: text,
        attribQueryParam: text.length
            ? getQueryParamValue(orgId, text, prefix)
            : null,
        copyMessageText: 'Copy',
        isCustom: true,
        order: config.order || 0
    };
};

export const getQueryParamValue = (
    orgId: number,
    text: string,
    prefix: string = null
) => {
    return `${orgId}${prefix !== null ? `-${prefix}` : ''}-${text}`;
};

const _attribDataValidates = (data: object) => {
    return !data.text || !data.text.length;
};

export const attribFactory = (
    data: object,
    isCustom: boolean = false,
    orgId: number | null = null
) => {
    const transformed = [];
    data.forEach((linkData: array) => {
        if (_attribDataValidates(data)) {
            transformed.push(createAttribObject(linkData, isCustom, orgId));
        }
    });
    return transformed;
};

export const save = async (
    customAttribs: array,
    deletedAttribIds: array = []
) => {
    const data = customAttribs.map((attrib: object) => {
        return {
            text: `${CUSTOM_LINK_PREFIX}-${attrib.customText}`,
            id: attrib.attribId,
            order: attrib.order || 0
        };
    });
    const existingAttribs = data.filter(
        (a: object) => !isNaN(a.id) && !deletedAttribIds.includes(a.id)
    );
    const newAttribs = data.filter((a: object) => isNaN(a.id));
    const responseUpdateDelete = await updateCustomAttributionLinks({
        attributions: existingAttribs,
        itemIdsToSoftDelete: deletedAttribIds
    });
    let responseAdd = [];
    if (newAttribs && newAttribs.length) {
        responseAdd = await addCustomAttributionLinks({
            attributions: newAttribs
        });
    }
    const refreshedAttribs = [...responseUpdateDelete, ...responseAdd];
    return refreshedAttribs;
};

export const getPredefinedAttribs = () => {
    return PREDEFINED_ATTRIBS;
};

export const getFullUrl = (
    attrib: object,
    urlPrefix: string,
    paramSeparator: string = '?',
    attribPrefix: string = null
) => {
    return `${urlPrefix}${paramSeparator}attrib=${getQueryParamValue(
        attrib.orgId,
        attrib.customText || attrib.typeSlug,
        attribPrefix
    )}`;
};

const _getHash = () =>
    Math.random()
        .toString(36)
        .substr(2, 9);

export default {
    createAttribObject,
    attribFactory,
    save,
    getPredefinedAttribs,
    getQueryParamValue,
    getFullUrl
};



// WEBPACK FOOTER //
// ./src/view/my-store/components/attribUtil.js